import { type ReactNode, useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useT } from '../../i18n';
import { usePlatform } from '../../platform/platform';
import { readPref, writePref } from '../../shared/clientPrefs';
import { activeNavId, moreItemsFor, navItemsFor } from '../../layout/navItems';
import { useNavFocus } from '../../layout/useNavFocus';
import { type SizeClass, useHeightClass, useSizeClass } from '../../layout/sizeClass';
import { useRovingTabindex } from '../../hooks/useRovingTabindex';
import { Icon } from '../ui/Icon';
import styles from './AppNav.module.css';

// ============================================================================
// AppNav (E2/Paket B·G·H·I) — EINE Navigation in DREI Gestalten, EIN DOM.
// docs/30 §6: `data-shape="bar|rail|sidebar"` + CSS machen den Rest. Kein
// zweiter Renderzweig. Unter 600 px die Handy-Bar (identisch zur bisherigen
// BottomNav, Kontaktbogen-Anker), ab 600 px die Icon-Rail (76 px), ab 1600 px
// per Default die Sidebar (272 px). Die Tour-Anker `bottom-nav` und `nav-<pfad>`
// werden in ALLEN Gestalten emittiert (sonst bricht die Tindra-Tour ab medium).
// ============================================================================

export type NavShape = 'bar' | 'rail' | 'sidebar';
export type RailState = 'hidden' | 'icon' | 'full';

export interface AppNavProps {
  /** Erzwingt eine Gestalt (der BottomNav-Reexport nutzt 'bar'). */
  shape?: NavShape;
  /** 'Am Tisch' / Rollen / Verlauf — nur bei shape !== 'bar', kollabiert bei leer. */
  contextSlot?: ReactNode;
}

function cx(...classes: (string | false | null | undefined)[]): string {
  return classes.filter(Boolean).join(' ');
}

// ----------------------------------------------------------------------------
// Rail-Zustand (Paket G): pro Groessenklasse persistiert, Cookie+localStorage
// (WKWebView schreibt document.cookie nicht auf Platte — clientPrefs.ts). Ein
// gemeinsamer Modul-Store, damit Rail (AppNav) und Umschalter (AppShell) exakt
// denselben Zustand teilen.
// ----------------------------------------------------------------------------

const RAIL_STATES: RailState[] = ['hidden', 'icon', 'full'];

function railPrefKey(size: SizeClass): string {
  return `lorath_rail_${size}`;
}

/** Default je Klasse (docs/30 §5): icon bis 1599, full ab 1600. */
function defaultRailState(): RailState {
  if (typeof window !== 'undefined' && window.innerWidth >= 1600) return 'full';
  return 'icon';
}

const overrides = new Map<SizeClass, RailState>();
const railSubscribers = new Set<() => void>();

function readRailState(size: SizeClass): RailState {
  const cached = overrides.get(size);
  if (cached) return cached;
  const stored = readPref(railPrefKey(size));
  const value: RailState = stored && (RAIL_STATES as string[]).includes(stored)
    ? (stored as RailState)
    : defaultRailState();
  overrides.set(size, value);
  return value;
}

function writeRailState(size: SizeClass, state: RailState): void {
  overrides.set(size, state);
  writePref(railPrefKey(size), state);
  for (const cb of railSubscribers) cb();
}

// Nur fuer Tests: der Modul-Cache wuerde sonst zwischen Testfaellen derselben
// Datei leaken (REVIEW-P19 Finding 5) — beforeEach ruft das hier auf.
export function __resetRailForTest(): void {
  overrides.clear();
}

function subscribeRail(cb: () => void): () => void {
  railSubscribers.add(cb);
  return () => {
    railSubscribers.delete(cb);
  };
}

function shapeFor(size: SizeClass, state: RailState): NavShape {
  if (size === 'compact') return 'bar';
  return state === 'full' ? 'sidebar' : 'rail';
}

export interface NavShapeApi {
  shape: NavShape;
  state: RailState;
  /** Durchschalten full → icon → hidden → full. */
  toggle: () => void;
  set: (next: RailState) => void;
  /** Ein Dreispalter drueckt die Rail auf 'icon' (ausser der Nutzer will 'full'). */
  autoCollapse: (reason: 'third-column') => void;
}

/**
 * Die eine Quelle fuer Rail-Gestalt und -Zustand. Von AppNav (Rendern) und
 * AppShell (Umschalt-Knopf, html[data-rail]) gemeinsam genutzt.
 */
export function useNavShape(): NavShapeApi {
  const size = useSizeClass();
  const height = useHeightClass();
  const state = useSyncExternalStore(
    subscribeRail,
    () => readRailState(size),
    () => 'icon' as RailState,
  );
  // height wird bewusst gelesen (Abo), damit spaetere h-short-Regeln greifen;
  // in E2 aendert sie die Gestalt noch nicht (Handy quer bleibt Bar).
  void height;

  const set = useCallback((next: RailState) => writeRailState(size, next), [size]);
  const toggle = useCallback(() => {
    const order: RailState[] = ['full', 'icon', 'hidden'];
    const idx = order.indexOf(readRailState(size));
    writeRailState(size, order[(idx + 1) % order.length]);
  }, [size]);
  const autoCollapse = useCallback(() => {
    if (readRailState(size) === 'full') writeRailState(size, 'icon');
  }, [size]);

  return { shape: shapeFor(size, state), state, toggle, set, autoCollapse };
}

export function AppNav({ shape: forcedShape, contextSlot }: AppNavProps) {
  const { t } = useT();
  const platform = usePlatform();
  const location = useLocation();
  const { shape: derivedShape, state } = useNavShape();
  const shape = forcedShape ?? derivedShape;
  const pathname = location.pathname;
  const activeId = activeNavId(pathname);
  // W2-B/X-09 (E-07): Neukonten sehen die fokussierte Leiste (navItems.ts).
  const navFocused = useNavFocus();

  // html[data-rail] fuer die layout.css-Breitenschaltung (--pane-rail 76/272/0).
  // Nur die Rail-Gestalten schreiben es; die Bar hat keine Rail.
  useEffect(() => {
    if (typeof document === 'undefined' || shape === 'bar') return undefined;
    const root = document.documentElement;
    const prev = root.getAttribute('data-rail');
    root.setAttribute('data-rail', state);
    return () => {
      if (prev === null) root.removeAttribute('data-rail');
      else root.setAttribute('data-rail', prev);
    };
  }, [shape, state]);

  // adminOnly-Eintraege werden in E2 nicht gerendert (Auth-Flag-Verdrahtung
  // spaeter) — sie bleiben in NAV_ITEMS fuer die matches()-Abdeckung von /admin.
  // In der Bar (Handy) NUR die 5 primaeren Eintraege — damit der Kontaktbogen
  // unter 600 px bit-identisch zur bisherigen BottomNav bleibt (die Sekundaer-
  // Sektionen werden nicht ins DOM gerendert, nicht nur CSS-versteckt). Rail und
  // Sidebar zeigen zusaetzlich Werkzeuge/Konto.
  // W2-B/X-09: `barOnly` („Mehr") nur in der Bar — Rail/Sidebar zeigen die
  // Bereiche ohnehin vollstaendig.
  const visibleItems = navItemsFor(navFocused).filter((item) => {
    if (item.adminOnly) return false;
    if (shape === 'bar') return item.primary;
    return !item.barOnly;
  });
  const hasContext = shape !== 'bar' && contextSlot != null;
  // W2-B/X-09: Steht der Nutzer in einem Bereich, den die fokussierte Bar nicht
  // zeigt (Welt, Tafelrunde, Einstellungen …), markiert „Mehr" den Ort — sonst
  // waere in der Bar gar nichts aktiv und der Rueckweg unklar.
  const shownActiveId = activeId && !visibleItems.some((item) => item.id === activeId)
    && visibleItems.some((item) => item.id === 'more')
    && moreItemsFor(navFocused).some((item) => item.id === activeId)
    ? 'more'
    : activeId;

  // ── Roving-Tabindex (E4a/E4b/Paket D) — die Nav ist EIN Tab-Stop ──────────
  // Ein INNERER role="toolbar"-Wrapper (styles.toolbar) traegt die Roving-
  // Container-Props: tabindex=0, aria-activedescendant, onKeyDown, onFocus,
  // aria-label und role="toolbar". Er umschliesst NUR die NavLinks; die
  // Kontext-Sektion (styles.context) bleibt direktes Kind von <nav>, ausserhalb
  // des Toolbars. Das <nav> behaelt seine implizite Landmarke "navigation" +
  // sein aria-label + alle Styles/Data-Attribute.
  //
  // Warum ueberhaupt der Wrapper (E4a-Schuld): aria-activedescendant ist auf
  // role="navigation" laut ARIA unzulaessig — Screenreader ignorierten den
  // Deskriptor. role="toolbar" ist ein gueltiger Container fuer den Deskriptor.
  //
  // Pixel-neutral by construction: styles.toolbar ist `display: contents` — der
  // Wrapper erzeugt KEINE Box, die NavLinks nehmen weiter exakt an der Flex-
  // Anordnung des <nav> teil (bar: row space-around; rail/sidebar: column).
  //
  // WKWebView 15.4+ (iOS-Mindestziel): role="toolbar" + aria-activedescendant
  // werden von VoiceOver korrekt ausgewertet; die Enter/Pfeil-Steuerung laeuft
  // ueber onKeyDown des Wrappers, unabhaengig vom Fokus-Modell des Browsers.
  //
  // Die NavLinks sind tabindex=-1. Pfeile bewegen den Deskriptor OHNE zu
  // navigieren, Enter/Klick navigiert. Der Deskriptor startet auf dem aktiven
  // Routen-Eintrag; das eigene aria-current="page" der NavLinks bleibt
  // unangetastet.
  const navigate = useNavigate();
  const linkDomId = (id: string) => `nav-link-${id}`;
  const [navFocusId, setNavFocusId] = useState<string | null>(null);
  // E4a-Verify: nach Routenwechsel (Klick/Deep-Link/Back) faellt der Deskriptor
  // auf den aktiven Routen-Eintrag zurueck — sonst haengt aria-activedescendant
  // nach der ersten Pfeilbedienung fuer immer am zuletzt gepfeilten Eintrag.
  useEffect(() => {
    setNavFocusId(null);
  }, [pathname]);
  // Der aktive Deskriptor: bewusste Pfeil-Auswahl, sonst der aktive Routen-
  // Eintrag, sonst der erste sichtbare Eintrag.
  const rovingActiveId = navFocusId
    ?? (shownActiveId && visibleItems.some((i) => i.id === shownActiveId) ? shownActiveId : visibleItems[0]?.id ?? null);

  const { containerProps } = useRovingTabindex({
    itemIds: visibleItems.map((item) => linkDomId(item.id)),
    role: 'toolbar',
    ariaLabel: t('nav.menu'),
    orientation: shape === 'bar' ? 'horizontal' : 'vertical',
    activeId: rovingActiveId ? linkDomId(rovingActiveId) : null,
    onActiveChange: (domId) => {
      const item = visibleItems.find((i) => linkDomId(i.id) === domId);
      if (item) setNavFocusId(item.id);
    },
    onActivate: (domId) => {
      const item = visibleItems.find((i) => linkDomId(i.id) === domId);
      if (item) navigate(item.to);
    },
  });

  return (
    // data-tour-anchor="bottom-nav" liegt in ALLEN Gestalten auf dem Container
    // (Tindra-Tour, docs/30 §6). Der Name bleibt historisch 'bottom-nav'.
    // Struktur seit E4b: nav > div.toolbar > NavLink — der Toolbar-Wrapper ist
    // eine ECHTE Box (Fokus!) und traegt die innere Anordnung; Pixel-Paritaet
    // der Bar haengt am Kontaktbogen, nicht mehr an der Flachheit.
    <nav
      className={styles.nav}
      data-shape={shape}
      data-rail-state={shape === 'bar' ? undefined : state}
      aria-label={t('nav.menu')}
      data-platform={platform.os}
      data-tour-anchor="bottom-nav"
    >
      {/*
        Innerer role="toolbar"-Wrapper (E4b): traegt die Roving-Container-Props
        (role="toolbar", tabindex, aria-activedescendant, aria-label, onKeyDown,
        onFocus). styles.toolbar = display:contents → keine eigene Box, die
        NavLinks bleiben pixelgleich in der Flex-Anordnung des <nav>. Umschliesst
        NUR die NavLinks; die Kontext-Sektion steht ausserhalb.
        descriptorId (aria-activedescendant) kommt mit containerProps.
      */}
      <div className={styles.toolbar} {...containerProps}>
        {visibleItems.map((item) => {
          const active = shownActiveId === item.id;
          const label = t(item.labelKey);
          return (
            <NavLink
              key={item.id}
              id={linkDomId(item.id)}
              to={item.to}
              className={cx(styles.item, active && styles.active)}
              aria-current={active ? 'page' : undefined}
              tabIndex={-1}
              title={label}
              data-section={item.section}
              // Fallback-Anker fuer die Tindra-Tour (frontend/src/tour/steps.ts).
              data-tour-anchor={`nav-${item.to.replace(/^\//, '').replace(/\//g, '-')}`}
            >
              <Icon name={item.icon} size={24} className={styles.icon} />
              <span className={styles.label}>{label}</span>
            </NavLink>
          );
        })}
      </div>
      {hasContext ? <div className={styles.context}>{contextSlot}</div> : null}
    </nav>
  );
}
